import { useState } from "react";
import { router } from "@inertiajs/react";
import { AccountLayout } from "@/views/account/AccountLayout";
import { Field } from "@/components/Field";
import { PasswordInput } from "@/components/PasswordInput";
import { FormSection, FormFields, FormRow } from "@/components/FormSection";
import { SectionSaveBar } from "@/components/SectionSaveBar";

interface Account {
  name: string;
  email: string;
  provider: string | null;
}

interface Props {
  account: Account;
}

const EMPTY = { current: "", next: "", confirm: "" };

export default function AccountSecurity(_props: Props) {
  const [form, setForm] = useState(EMPTY);
  const [serverError, setServerError] = useState<string | null>(null);

  const { current, next, confirm } = form;
  const dirty = current !== "" || next !== "" || confirm !== "";
  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm;

  const set = (patch: Partial<typeof EMPTY>) => setForm((f) => ({ ...f, ...patch }));

  function discard() {
    setForm(EMPTY);
    setServerError(null);
  }

  function save(): Promise<void> {
    setServerError(null);
    if (!canSubmit) return Promise.resolve();
    return new Promise((resolve) => {
      router.post(
        "/account/password",
        { current_password: current, password: next, password_confirmation: confirm },
        {
          preserveScroll: true,
          onSuccess: () => discard(), // server flash "Password updated." drives the toast
          onError: (errors) => setServerError(errors.base || "Could not update your password."),
          onFinish: () => resolve(),
        },
      );
    });
  }

  return (
    <AccountLayout title="Security">
      <FormSection id="password" title="Change password" description="Use at least 8 characters.">
        <FormFields>
          <FormRow label="Current password" htmlFor="current-password">
            <Field.Root>
              <PasswordInput
                id="current-password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => set({ current: e.target.value })}
                placeholder="Current password"
              />
            </Field.Root>
          </FormRow>
          <FormRow label="New password" htmlFor="new-password">
            <Field.Root error={tooShort}>
              <PasswordInput
                id="new-password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => set({ next: e.target.value })}
                placeholder="New password"
              />
              {tooShort && <Field.Error>Must be at least 8 characters.</Field.Error>}
            </Field.Root>
          </FormRow>
          <FormRow label="Confirm new password" htmlFor="confirm-password">
            <Field.Root error={mismatch}>
              <PasswordInput
                id="confirm-password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => set({ confirm: e.target.value })}
                placeholder="Re-enter new password"
              />
              {mismatch && <Field.Error>Passwords do not match.</Field.Error>}
            </Field.Root>
          </FormRow>
          {serverError && (
            <div className="px-4 py-3">
              <p className="text-sm text-danger-fg">{serverError}</p>
            </div>
          )}
        </FormFields>
      </FormSection>

      <SectionSaveBar
        dirty={dirty}
        canSave={canSubmit}
        onSave={save}
        onDiscard={discard}
        saveLabel="Update password"
      />
    </AccountLayout>
  );
}
